/* eslint-disable prefer-promise-reject-errors */
/* global Image */

import { getStorage, ref, getDownloadURL, uploadBytes, uploadString, deleteObject, getBlob } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { firebase, storage } from '$lib/firebase/firebase';
// eslint-disable-next-line no-unused-vars
import { tenantID } from '@repo/config/Settings';
import { parse } from 'zipson';
import { localStorageAvailable } from '@repo/config/Utils';
import { addFileCacheMarker, getFileCacheMarker} from '$lib/DB';
import md5 from 'md5';

// Create session image cache
const imageCache = {};

function getTenantCachedImageDownloadURL(filename) {
    return new Promise((resolve, reject) => {
        if (!filename) reject('Filename Undefined');
        getDownloadURL(ref(storage, `tenant-files/${tenantID}/cache/images/${filename}`)).then(resolve).catch(reject);
    });
}

function putTenantCachedImage(data) {
    return new Promise((resolve, reject) => {
        uploadBytes(ref(storage, `tenant-files/${tenantID}/cache/images/${data.filename}`), data.data).then(resolve).catch(reject);
    });
}

function putTenantCachedImageFromString(data) {
    return new Promise((resolve, reject) => {
        uploadString(ref(storage, `tenant-files/${tenantID}/cache/images/${data.filename}`), data.data).then(resolve).catch(reject);
    });
}

function getTenantCachedImageFromString(filename) {
    return new Promise((resolve, reject) => {
        const fn = filename;
        if (imageCache[fn]) {
            resolve(imageCache[fn]);
        } else {
            getBlob(ref(storage, `tenant-files/${tenantID}/cache/images/${fn}`)).then(blob => {
                blob.text().then(txt => {
                    imageCache[fn] = txt;
                    resolve(imageCache[fn]);
                }).catch(reject);
            }).catch(reject);
        }
    });
}

function deleteTenantCachedImage(data) {
    return new Promise((resolve, reject) => {
        deleteObject(ref(storage, `tenant-files/${tenantID}/cache/images/${data.filename}`)).then(resolve).catch(reject);
    });
}

function getTenantLocalCachedCollectionTree(id) {
    return new Promise((resolve, reject) => {
        const p = `tenant-files/${tenantID}/cache/trees/tree_${id}`;
        const f = `file_${md5(p)}`;
        const fm = `file_marker_${md5(p)}`;
        
        // try local storage
        if (localStorageAvailable('localStorage')) {
            if (window.localStorage.getItem(f) && window.localStorage.getItem(fm)) {
                getFileCacheMarker({path: md5(p)}).then(m => {
                    if (m.exists()) {
                        if (m.data().marker.toString() === window.localStorage.getItem(fm).toString()) {
                            console.log('Did load tree from local');
                            resolve(parse(window.localStorage.getItem(f)));
                        } else {
                            resolve(getTenantServerCachedCollectionTree(p));
                        }
                    } else {
                        resolve(getTenantServerCachedCollectionTree(p));
                    }    
                }).catch(reject);        
            } else {
                resolve(getTenantServerCachedCollectionTree(p));
            }
        }
    });
}

function getTenantServerCachedCollectionTree(path) {
    return new Promise((resolve, reject) => {
        // if no cached version or version out of date, load a new copy from the server and save locally
        getFileCacheMarker({path: md5(path)}).then(() => {
            getBlob(ref(storage, path)).then(blob => {
                blob.text().then(txt => {
                    getFileCacheMarker({path: md5(path)}).then(res => {
                        if (res.exists()) {
                            // save to local storage
                            window.localStorage.setItem(`file_${md5(path)}`, txt);
                            window.localStorage.setItem(`file_marker_${md5(path)}`, res.data().marker);
                        }
                        resolve(parse(txt));
                    }).catch(reject);
                }).catch(reject);
            }).catch(reject);
        }).catch(reject);
    });
}

function putTenantCachedCollectionTree(data) {
    return new Promise((resolve, reject) => {
        const p = `tenant-files/${tenantID}/cache/trees/tree_${data.id}`;
        uploadString(ref(storage, p), data.data).then(() => {
            addFileCacheMarker({path: md5(p)}).then(res => {
                window.localStorage.setItem(`file_marker_${md5(p)}`, res);
                resolve();
            });
        }).catch(reject);
        
    });
}

/**
 * Resolve a thumbnailURI or location path to a usable image URL.
 * Handles data URIs, full URLs, and relative Firebase Storage paths.
 */
const storageUrlCache = {};
async function resolveStorageUrl(path) {
    if (!path) return '';
    if (path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    if (storageUrlCache[path]) return storageUrlCache[path];
    const url = await getDownloadURL(ref(storage, path));
    storageUrlCache[path] = url;
    return url;
}

/**
 * Resolve placeholder icon for a resource catalog type.
 * Tries SVG first (preferred format), then falls back to PNG and WebP in parallel.
 * Caches results to avoid repeated lookups.
 */
const THUMB_FORMATS = ['.svg', '.png', '.webp'];
const placeholderCache = new Map();

async function resolvePlaceholder(catalog) {
    let key = 'default'; // default placeholder
    switch (String(catalog || '').toLowerCase()) {
        case 'weblinks':
        case 'weblink':
            key = 'weblinks';
            break;
        case '3d model':
        case '3dmodel':
            key = '3d-model';
            break;
        case 'download':
            key = 'pdf';
            break;
        case 'spreadsheet':
            key = 'spreadsheet';
            break;
        case 'video':
            key = 'video';
            break;
        case 'game':
            key = 'game';
            break;
    }
    if (placeholderCache.has(key)) return placeholderCache.get(key);

    const promise = (async () => {
        const slug = key.toLowerCase().split(' ').join('-');

        const testImageLoad = (url) => new Promise((resolve) => {
            const img = new Image();
            img.src = url;
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            setTimeout(() => resolve(false), 2000);
        });

        // Try SVG first
        try {
            const svgUrl = await getDownloadURL(ref(storage, `thumbnails/resource-hub-${slug}.svg`));
            if (await testImageLoad(svgUrl)) return svgUrl;
        } catch (err) {
            // SVG doesn't exist, proceed to fallbacks
        }

        // If SVG fails, try PNG and WebP in parallel
        const fallbackFormats = ['.png', '.webp'];
        const fallbackPromises = fallbackFormats.map(async (ext) => {
            try {
                const url = await getDownloadURL(ref(storage, `thumbnails/resource-hub-${slug}${ext}`));
                if (await testImageLoad(url)) return url;
            } catch (err) {
                // Format doesn't exist
            }
            return null;
        });

        const results = await Promise.all(fallbackPromises);
        return results.find((url) => url !== null) || '';
    })();

    placeholderCache.set(key, promise);
    return promise;
}

/**
 * Get a Firebase Storage instance for an alternate bucket.
 * Falls back to the default exported `storage` when bucketUrl is omitted.
 * @param {string} [bucketUrl] - Optional bucket URL.
 * @returns {import('firebase/storage').FirebaseStorage}
 */
function getStorageInstance(bucketUrl) {
    if (!bucketUrl) return storage;
    return getStorage(firebase, bucketUrl);
}

/**
 * Wrapper around getDownloadURL that retries once on 403 after forcing a
 * token refresh. Custom claims (e.g. accessibleBooks) may not be present in
 * the ID token after client-side navigation; a forced refresh pulls them in
 * so the Storage security rules can evaluate correctly.
 */
async function getDownloadURLWithRefresh(storageRef) {
    try {
        return await getDownloadURL(storageRef);
    } catch (error) {
        if (error?.code === 'storage/unauthorized') {
            const user = getAuth().currentUser;
            if (user) {
                await user.getIdToken(true);
                return await getDownloadURL(storageRef);
            }
        }
        throw error;
    }
}

export {
    getStorageInstance,
    getDownloadURLWithRefresh,
    getTenantCachedImageDownloadURL,
    putTenantCachedImage,
    putTenantCachedImageFromString,
    getTenantCachedImageFromString,
    deleteTenantCachedImage,
    getTenantLocalCachedCollectionTree,
    getTenantServerCachedCollectionTree,
    putTenantCachedCollectionTree,
    resolveStorageUrl,
    resolvePlaceholder,
};